import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import styles from './JobDetailNew.module.css';
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Loader2 from '../../components/loader2';
import showToast from "../../components/ShowToast";
import LocationInputGroup from "../../components/Form/LocationInputGroup";
import api from '../../api/api.js';
import ModernNavbarPureCSS from '../../components/Navbar/ModernNavBarPureCSS';

import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Home,
  FileText,
  Clock,
  AlertCircle,
  CheckCircle,
  MessageSquare, 
  X, 
} from "lucide-react";
import JobFileUploader from "../../components/Form/JobFileUploader";
import ProfessionSelector from "../../components/Form/ProfessionSelector";

const JobDetailNew = () => {
  const navigate = useNavigate();
  const { jobId } = useParams(); 
  const location = useLocation(); 

  const [jobTitle, setJobTitle] = useState("");
  const [postcode, setPostcode] = useState("");
  const [suburb, setSuburb] = useState("");
  const [mystate, setMyState] = useState("");
  const [address, setAddress] = useState(""); // Add address state
  const [description, setDescription] = useState("");

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [homeUserId, setHomeUserId] = useState(null);
  
  const [selectedProfessions, setSelectedProfessions] = useState([]); 
  const [availableProfessions, setAvailableProfessions] = useState([]); 


  const [customBudget, setCustomBudget] = useState("");
  const [selectedBudgetRange, setSelectedBudgetRange] = useState("");
  const budgetRanges = [
    "$500 - $1000", "$1000 - $2500", "$2500 - $5000",
    "$5000 - $10000", "$10000 - $25000", "$25000 - $50000", "$50000+",
  ];

  const [selectedTimeline, setSelectedTimeline] = useState("");
  const timelineOptions = [
    { value: "Urgently", label: "Urgently", icon: AlertCircle, colorClass: styles.jobDetailNew_bgRed500 },
    { value: "Within 1 week", label: "Within 1 week", icon: Clock, colorClass: styles.jobDetailNew_bgBlue500 },
    { value: "Within 2 weeks", label: "Within 2 weeks", icon: Calendar, colorClass: styles.jobDetailNew_bgGreen500 },
    { value: "2 weeks or longer", label: "2 weeks or longer", icon: Calendar, colorClass: styles.jobDetailNew_bgPurple500 },
  ];

  const [siteVisit, setSiteVisit] = useState("Anytime");
  const siteVisitTimes = ["Anytime", "Weekdays only", "Weekends only", "Evenings", "By appointment"];

  const [uploadedImages, setUploadedImages] = useState([]); 

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [currentJobData, setCurrentJobData] = useState(null); 
  const [isPublishMode, setIsPublishMode] = useState(true); 

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageInModal, setCurrentImageInModal] = useState(0);

  const [username, setUsername] = useState("");


  const handlePostcodeSelect = (location) => {
    console.log("Selected Location:", location);
    setPostcode(location.postcode); // Set postcode only
    setMyState(location.state); 
    setSuburb(location.suburb);
    setAddress(location.fullAddress); // Set full address
  };


  const openImageModal = (index) => {
    setCurrentImageInModal(index);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  const nextImageInModal = () => {
    setCurrentImageInModal((prevIndex) => (prevIndex + 1) % uploadedImages.length);
  };

  const prevImageInModal = () => {
    setCurrentImageInModal((prevIndex) => (prevIndex - 1 + uploadedImages.length) % uploadedImages.length);
  };

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (jobId) {
        setIsLoading(true);
        setError(null);
        try {
          const response = await axios.get(`${API_BASE_URL}/api/Jobs/getjobbyid?id=${jobId}`);
          const jobData = response.data;
          setCurrentJobData(jobData); 

          setJobTitle(jobData.name || "");
          setPostcode(jobData.postcode || "");
          setSuburb(jobData.suburb || "");
          setMyState(jobData.mystate || "");
          setAddress(jobData.address || jobData.Address || ""); // Set address
          setDescription(jobData.description || "");
          
          // --- FIX 1: Robust mapping for existing professions ---
          // Ensures we handle casing differences from API
          const loadedProfessions = jobData.profession ? jobData.profession.map(p => ({ 
              professionId: p.professionId || p.ProfessionId || p.id, // Check all possible casings
              name: p.name 
          })) : [];
          
          setSelectedProfessions(loadedProfessions);
          // ---------------------------------------------------

          setCustomBudget(jobData.budgetAmount ? String(jobData.budgetAmount) : "");
          setSelectedBudgetRange(jobData.budget || "");
          setSelectedTimeline(jobData.deadline || "");
          setSiteVisit(jobData.siteVisit || "Anytime");
          
          if (!location.state?.projectId) {
            setSelectedProjectId(jobData.projectId ? String(jobData.projectId) : "");
          }

          const initialUploadedImageObjects = [];
          for (let i = 1; i <= 4; i++) {
            const imageUrl = jobData[`image${i}`];
            if (imageUrl && imageUrl !== "https://api.buildstart.au") {
              initialUploadedImageObjects.push({
                key: `existing-${i}-${imageUrl.slice(-10)}`,
                url: imageUrl,
                file: null,
                isExisting: true,
                originalServerKey: `image${i}`
              });
            }
          }
          setUploadedImages(initialUploadedImageObjects);

          const token = localStorage.getItem('token');
          if (token) {
            const decodeToken = jwtDecode(token);
            const loggedInUserId = String(decodeToken.nameid);
            const jobOwnerId = String(jobData.userId);
            setUsername(decodeToken.UserName || decodeToken.UserName || "User");

            if (jobOwnerId !== loggedInUserId) {
              setIsReadOnly(true);
              showToast('info', "You are viewing this job in read-only mode as you are not the owner.");
            } else {
              setIsReadOnly(false);
            }
          } else {
            setIsReadOnly(true);
            showToast('info', "Viewing job in read-only mode. Please log in to edit.");
          }

        } catch (err) {
          console.error("Error fetching job details:", err);
          setError(err.message || "Failed to fetch job details.");
          showToast('error', `Error fetching job: ${err.message}`);
          navigate(`/job-console/${jobId}`);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Reset state
        setJobTitle("");
        setPostcode("");
        setMyState("");
        setAddress(""); // Reset address
        setDescription("");
        setSelectedProfessions([]);
        setCustomBudget("");
        setSelectedBudgetRange("");
        setSelectedTimeline("");
        setSiteVisit("Anytime");
        setUploadedImages([]);
        setIsReadOnly(false);
        setCurrentJobData(null);
      }
    };

    fetchJobDetails();
  }, [jobId, navigate]);

  useEffect(() => {
    const fetchTopProfessions = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/ProfessionSearch/searchtop20professions`);
        const { tradespersons, buildingProfessionals } = response.data;
        
        // --- FIX 2: Check for 'id' OR 'professionId' ---
        // Most search APIs return 'id', if we only map 'professionId' it becomes undefined
        const mapProfession = (p) => ({
            professionId: p.professionId || p.id, 
            name: p.name,
            type: p.professionType
        });

        const topTradespersons = tradespersons.slice(0, 10).map(mapProfession);
        const topBuildingProfessionals = buildingProfessionals.slice(0, 10).map(mapProfession);
        setAvailableProfessions([...topTradespersons, ...topBuildingProfessionals]);
        // ---------------------------------------------
      } catch (err) {
        console.error("Error fetching top professions:", err);
        // Fallback hardcoded list is safe because it uses specific 'professionId' keys
        setAvailableProfessions([
          { professionId: 158, name: "Quantity Surveyor", type: "building_professional" }, { professionId: 18, name: "Builder", type: "tradesperson" }, 
          { professionId: 70, name: "Electrician", type: "tradesperson" }, { professionId: 145, name: "Plumber", type: "tradesperson" },
          { professionId: 33, name: "Carpenter", type: "tradesperson" }, { professionId: 136, name: "Painter", type: "tradesperson" },
          { professionId: 124, name: "Landscaper", type: "tradesperson" }, { professionId: 166, name: "Roofer", type: "tradesperson" },
          { professionId: 202, name: "Tiler", type: "tradesperson" }, { professionId: 119, name: "Kitchen Designer", type: "building_professional" },
          { professionId: 6, name: "Architect", type: "building_professional" }, { professionId: 110, name: "Interior Designer", type: "building_professional" }
        ]);
      }
    };
    fetchTopProfessions();

    const fetchUserProjects = async (userId) => {
      if (!userId) return;
      try {
        const response = await api.get(`/api/Projects/getmyprojects?homeUserId=${userId}`);
        setProjects(response.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setProjects([]); 
      }
    };

    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      const currentHomeUserId = decodedToken.nameid;
      setHomeUserId(currentHomeUserId);
      fetchUserProjects(currentHomeUserId);
    }

    const projectFromState = location.state?.projectId;
    if (projectFromState) {
      setSelectedProjectId(String(projectFromState));
    }

    if (location.state?.mode === 'invite') {
      setIsPublishMode(false);
    } else {
      setIsPublishMode(true);
    }

  }, [jobId, location.state]); 

  const handleSubmit = async (isPublish = true) => {
    if (jobTitle.trim().length < 10) { showToast('error', 'Job title must be at least 10 characters long.'); return; }
    if (description.trim().length < 10) { showToast('error', 'Job description must be at least 10 characters long.'); return; }
    if (selectedProfessions.length === 0) { showToast('error', 'Please select at least one profession.'); return; }
    if (!postcode.trim()) { showToast('error', 'Please enter a postcode.'); return; }
    if (!customBudget.trim() && !selectedBudgetRange) { showToast('error', 'Please provide a budget (custom amount or range).'); return; }
    if (!selectedTimeline) { showToast('error', 'Please select a project timeline.'); return; }
    if (!siteVisit) { showToast('error', 'Please select site visit availability.'); return; }

    setIsLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      showToast('error', 'Please Login to create or update a job.');
      setIsLoading(false);
      return;
    }

    try {
      const decodeToken = jwtDecode(token);
      const homeUserIdFromToken = parseInt(decodeToken.nameid, 10);
      setHomeUserId(homeUserIdFromToken); 

      // Clean postcode
      const cleanPostcode = postcode.match(/\d{4}/)?.[0] || postcode;

      // --- FIX 3: Filter invalid Professions ---
      // 1. Filter out any profession where ID is missing or 0.
      // 2. Map correctly to integer.
      const validProfessions = selectedProfessions
        .filter(p => p.professionId && !isNaN(parseInt(p.professionId, 10)) && parseInt(p.professionId, 10) > 0)
        .map(p => ({ 
            ProfessionId: parseInt(p.professionId, 10), 
            name: p.name 
        }));
      
      if (validProfessions.length === 0) {
          showToast('error', "Invalid professions selected. Please remove and re-add them.");
          setIsLoading(false);
          return;
      }
      // ----------------------------------------

      if (jobId) { // Edit operation
        
        const jobDataToUpdate = {
          job_id: parseInt(jobId, 10),
          projectId: selectedProjectId ? parseInt(selectedProjectId, 10) : null,
          userId: homeUserIdFromToken,
          name: jobTitle,
          profession: validProfessions, // Use the validated list
          postcode: cleanPostcode,
          suburb: suburb,
          mystate: mystate,
          address: address, // Add Address
          description: description,
          budgetAmount: customBudget ? parseFloat(customBudget) : null,
          budget: selectedBudgetRange,
          deadlineDate: currentJobData?.deadlineDate || null,
          deadline: selectedTimeline,
          siteVisit: siteVisit,
          jobStatus: currentJobData?.jobStatus || "Open", 
          created_at: currentJobData?.created_at,
          updated_at: new Date().toISOString(),
          isPublish: isPublish,
          isNew: false,
          image1: null, image2: null, image3: null, image4: null,
        };

        for (let i = 0; i < 4; i++) {
          const imgObj = uploadedImages[i];
          if (imgObj) {
            if (imgObj.isExisting) {
              jobDataToUpdate[`image${i + 1}`] = imgObj.url.replace(/^https?:\/\/[^/]+\//, "/");
            } else {
              jobDataToUpdate[`image${i + 1}`] = ""; 
            }
          } else {
            jobDataToUpdate[`image${i + 1}`] = null;
          }
        }
        
        console.log("Submitting job update with payload:", jobDataToUpdate);
        await axios.post(`${API_BASE_URL}/api/Jobs/updatejob?id=${jobId}`, jobDataToUpdate);

        const newFilesFormData = new FormData();
        uploadedImages.forEach(imgObj => {
          if (imgObj.file && !imgObj.isExisting) {
            newFilesFormData.append("files", imgObj.file);
          }
        });

        if (newFilesFormData.has("files")) {
          console.log("Uploading new images for job:", jobId);
          await axios.post(`${API_BASE_URL}/api/Jobs/upload/${jobId}`, newFilesFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
        
        showToast('success', 'Job updated successfully!');
        
        console.log(`Navigating to job ${jobDataToUpdate.job_id}`);
        navigate(`/job-console/${jobDataToUpdate.job_id}`);


      } else { // Create new job operation

        const createJobPayload = {
            projectId: selectedProjectId ? parseInt(selectedProjectId, 10) : null,
            userId: homeUserIdFromToken,
            name: jobTitle,
            profession: validProfessions, // Use validated list
            postcode: cleanPostcode,
            suburb: suburb,
            mystate: mystate,
            address: address, // Add Address
            description: description,
            budgetAmount: customBudget ? parseFloat(customBudget) : null,
            budget: selectedBudgetRange,
            deadlineDate: null, 
            deadline: selectedTimeline,
            siteVisit: siteVisit,
            jobStatus: "Open", 
            created_at: new Date().toISOString(), 
            updated_at: new Date().toISOString(),
            isPublish: isPublish,
            isNew: true 
        };
        console.log("Creating new job with payload:", createJobPayload);
        const response = await api.post(`/api/Jobs/createjob`, createJobPayload);

        console.log(response);

        const newJobId = response.data.job_id;

        if (newJobId && uploadedImages.length > 0) {
          const formData = new FormData();
          uploadedImages.forEach(imgObj => {
            if (imgObj.file && !imgObj.isExisting) {
              formData.append("files", imgObj.file);
            }
          });
          
          if (formData.has("files")) {
              console.log("Uploading images for new job:", newJobId);
              await axios.post(`${API_BASE_URL}/api/Jobs/upload/${newJobId}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
          }
        }
        
        showToast('success', 'Job created successfully!');

        navigate(`/job-console/${jobId}`);
      }

    } catch (err) {
      console.error(`Error ${jobId ? 'updating' : 'creating'} job:`, err.response || err);
      const action = jobId ? 'update' : 'create';
      setError(err.response?.data?.message || err.message || `Failed to ${action} job.`);
      showToast('error', `Error: ${err.response?.data?.message || err.message || `An unexpected error occurred while trying to ${action} the job.`}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (



    <div className={`${styles.jobDetailNew_minHeightScreen} ${styles.jobDetailNew_bgGradientPage}`}>

    <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'home'} />



      
      <header className={styles.jobDetailNew_header}>
        <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_headerContent}`}>
          <div className={styles.jobDetailNew_headerLeft}>
            <button onClick={() => navigate(`/job-console/${jobId}`)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`} style={{color: 'white'}}>
              <ArrowLeft className={styles.jobDetailNew_textPrimaryBlueDark} />
            </button>
            <div className={styles.jobDetailNew_headerLogo}>
              <Home className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textPrimaryBlueDark}`} />
              <span className={styles.jobDetailNew_headerTitle}>Build Start</span>
            </div>
            <div className={styles.jobDetailNew_headerSeparator}></div>
            <div>
              <h1 className={styles.jobDetailNew_headerTitle}>{jobId ? "Edit Job Details" : "Create New Job"}</h1>
              <p className={styles.jobDetailNew_textBlue900}>{jobId ? "Update your job requirements" : "Enter your job details"}</p>
            </div>
          </div>
        </div>
      </header>

      <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_mainContent}`}>
        {isLoading && <Loader2 />}
        {error && <p style={{color: 'red'}}>{error}</p>}
        <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_lg_gridCols3}`}>
          <div className={`${styles.jobDetailNew_lg_colSpan2} ${styles.jobDetailNew_spaceY6}`}>
            
            {/* The prop isProfessionLocked is passed here. 
                If currentJobData exists and jobLeadCount > 0, it disables the inputs. */}
            <ProfessionSelector 
              selectedProfessions={selectedProfessions}
              onUpdate={setSelectedProfessions}
              isReadOnly={isReadOnly}
              suggestedProfessions={availableProfessions}
              isProfessionLocked={currentJobData?.jobLeadCount > 0} 
            />

            <div className={styles.jobDetailNew_card}>
               {/* Rest of the form remains unchanged... */}
               <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <FileText className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textBlue600}`} />
                  <span>Job Information</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                  <div>
                    <label htmlFor="jobTitle" className={styles.jobDetailNew_label}>Job Title</label>
                    <input id="jobTitle" type="text" placeholder="e.g., Kitchen Renovation" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
{/*
                  <div>
                    <label htmlFor="project" className={styles.jobDetailNew_label}>Assign to Project (Optional)</label>
                    <select id="project" value={selectedProjectId} onChange={(e) => setSelectedProjectId(e.target.value)} className={styles.jobDetailNew_selectTrigger} disabled={isReadOnly}>
                      <option value="">Select a project</option>
                      {projects.map((project) => (
                        <option key={project.projectId} value={project.projectId}>{project.name}</option>
                      ))}
                    </select>
                  </div>
*/}


                  <div>
{/*                    <label className={styles.jobDetailNew_label}>Location</label> */}
                    <LocationInputGroup 
                        value={address} // Use address as value
                        onLocationSelect={handlePostcodeSelect}
                        disabled={isReadOnly}
                        placeholder="Enter job address..."
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="description" className={styles.jobDetailNew_label}>Job Description</label>
                  <textarea id="description" placeholder="Describe your project in detail..." value={description} onChange={(e) => setDescription(e.target.value)} className={styles.jobDetailNew_textarea} rows={4} disabled={isReadOnly}></textarea>
                </div>
              </div>
            </div>

            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <DollarSign className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textGreen500}`} />
                  <span>Budget Information</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                  <div>
                    <label htmlFor="customBudget" className={styles.jobDetailNew_label}>Custom Budget Amount (Optional)</label>
                    <div className={styles.jobDetailNew_inputWithIconContainer}>
                      <DollarSign className={styles.jobDetailNew_inputIcon} />
                      <input id="customBudget" type="number" placeholder="Enter amount" value={customBudget} onChange={(e) => setCustomBudget(e.target.value)} className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`} disabled={isReadOnly} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="budgetRange" className={styles.jobDetailNew_label}>Budget Range</label>
                    <select id="budgetRange" value={selectedBudgetRange} onChange={(e) => setSelectedBudgetRange(e.target.value)} className={styles.jobDetailNew_selectTrigger} disabled={isReadOnly}>
                      <option value="">Select a range</option>
                      {budgetRanges.map((range) => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Clock className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textBlue600}`} />
                  <span>Project Timeline</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY4}`}>
                <label className={styles.jobDetailNew_label}>When do you need this job done by?</label>
                <div className={styles.jobDetailNew_timelineButtonGrid}>
                  {timelineOptions.map((option) => {
                    const IconComponent = option.icon;
                    return (
                      <button
                        type="button"
                        key={option.value}
                        className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_timelineButton} ${selectedTimeline === option.value ? option.colorClass : styles.jobDetailNew_buttonOutline}`}
                        onClick={() => !isReadOnly && setSelectedTimeline(option.value)}
                        disabled={isReadOnly}
                      >
                        <IconComponent className={styles.jobDetailNew_timelineButtonIcon} />
                        <span className={styles.jobDetailNew_timelineButtonText}>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            
            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Calendar className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textPurple500}`} />
                  <span>Site Visit Availability</span>
                </h3>
              </div>
              <div className={styles.jobDetailNew_cardContent}>
                <div>
                  <label htmlFor="siteVisit" className={styles.jobDetailNew_label}>When are you available for site visits?</label>
                  <select id="siteVisit" value={siteVisit} onChange={(e) => setSiteVisit(e.target.value)} className={styles.jobDetailNew_selectTrigger} disabled={isReadOnly}>
                    {siteVisitTimes.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <JobFileUploader 
              currentFiles={uploadedImages}
              onFilesChange={setUploadedImages}
              isReadOnly={isReadOnly}
            />
          </div>

          <div className={styles.jobDetailNew_spaceY6}>
            <div className={`${styles.jobDetailNew_card} ${styles.jobDetailNew_sidebarCard}`}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_sidebarHeader}`}>
                <h3 className={styles.jobDetailNew_sidebarTitle}>Job Summary</h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_sidebarContentSpaceY4}`}>
                <div className={styles.jobDetailNew_sidebarContentSpaceY3}>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>TITLE</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{jobTitle || "Not specified"}</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>LOCATION</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{address || `${suburb || ''}${suburb ? ', ' : ''}${postcode || ''}`.trim() || "Not specified"}</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>BUDGET</label>
                    <p className={`${styles.jobDetailNew_sidebarValue} ${styles.jobDetailNew_sidebarBudgetValue}`}>{customBudget ? `$${customBudget}` : (selectedBudgetRange || "Not specified")}</p>
                    {customBudget && selectedBudgetRange && <p className={styles.jobDetailNew_sidebarBudgetRange}>Range: {selectedBudgetRange}</p>}
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>TIMELINE</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{selectedTimeline || "Not specified"}</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>PROFESSIONALS</label>
                    <div className={styles.jobDetailNew_flexWrap} style={{marginTop: '0.25rem'}}>
                      {selectedProfessions.length > 0 ? selectedProfessions.map((prof) => (
                        <span key={prof.professionId} className={`${styles.jobDetailNew_badge} ${styles.jobDetailNew_sidebarProfessionBadge}`}>
                          {prof.name}
                        </span>
                      )) : <p className={styles.jobDetailNew_sidebarValue}>Not specified</p>}
                    </div>
                  </div>
                  {currentJobData?.assignedBusinessId && currentJobData?.businessName && (
                    <div>
                      <label className={styles.jobDetailNew_sidebarLabel}>ASSIGNED TO</label>
                      <div className={styles.jobDetailNew_assignedBusinessInfo}>
                        {/* You might want to add an avatar or logo if available in currentJobData.businessProfilePic */}
                        <p className={styles.jobDetailNew_sidebarValue}>{currentJobData.businessName}</p>
                        <span className={`${styles.jobDetailNew_badge} ${styles.jobDetailNew_assignedBadge}`}>Assigned</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.jobDetailNew_separator}></div>
                <div className={styles.jobDetailNew_sidebarContentSpaceY3}>
                  {!isReadOnly && jobId && currentJobData && currentJobData.quoteCount > 0 && (
                    <button 
                      type="button" 
                      onClick={() => navigate(`/job/${jobId}/quotes`)} 
                      className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonSecondary}`} // Using a secondary style, adjust if needed
                    >
                      <MessageSquare className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                      View Quotes ({currentJobData.quoteCount})
                    </button>
                  )}
                  {!isReadOnly && (
                    <button type="button" onClick={() => handleSubmit(isPublishMode)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonPrimary}`}>
                      <CheckCircle className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                      {jobId ? "Save Changes" : (isPublishMode ? "Post Job (Broadcast)" : "Create Job (Invite Only)")}
                    </button>
                  )}
                  <button type="button" onClick={() => navigate(`/job-console/${jobId}`)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonOutline}`}>
                    <ArrowLeft className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                    Go Back
                  </button>
                </div>
              </div>
            </div>
            <div className={`${styles.jobDetailNew_card} ${styles.jobDetailNew_tipsCard}`}>
              <div className={styles.jobDetailNew_tipsContent}>
                <div className={styles.jobDetailNew_tipsEmoji}>💡</div>
                <h3 className={styles.jobDetailNew_tipsTitle}>Pro Tips</h3>
                <ul className={styles.jobDetailNew_tipsList}>
                  <li>• Add detailed descriptions for better quotes</li>
                  <li>• Upload clear photos of the work area</li>
                  <li>• Set realistic budgets and timelines</li>
                  <li>• Be specific about your requirements</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isImageModalOpen && uploadedImages.length > 0 && uploadedImages[currentImageInModal] && (
        <div className={styles.jobDetailNew_imageModalBackdrop} onClick={closeImageModal}>
          <div className={styles.jobDetailNew_imageModalContent} onClick={(e) => e.stopPropagation()}>
            <button onClick={closeImageModal} className={styles.jobDetailNew_imageModalCloseBtn}>
              <X size={24} />
            </button>
            <button onClick={prevImageInModal} className={`${styles.jobDetailNew_imageModalNavBtn} ${styles.jobDetailNew_imageModalPrevBtn}`}>
              {'<'}
            </button>
            <img src={uploadedImages[currentImageInModal].url} alt={`View ${currentImageInModal + 1}`} className={styles.jobDetailNew_imageModalImage} />
            <button onClick={nextImageInModal} className={`${styles.jobDetailNew_imageModalNavBtn} ${styles.jobDetailNew_imageModalNextBtn}`}>
              {'>'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetailNew;